/**
 * Argus Live — React hook that wires the pure-logic
 * `PostMessageHost` into the browser's `window.addEventListener('message', ...)`
 * surface and drives the visible iframe height.
 *
 * PR AL-B1. See `post-message-host-core.ts` for the threat model and
 * the cap definitions; this file is intentionally thin so the core can
 * be tested without a DOM.
 *
 * Lifecycle:
 *  1. Component renders the iframe with `ref={iframeRef}`.
 *  2. Hook mounts → registers `message` listener → waits for iframe
 *     load.
 *  3. On iframe `load`, hook calls `host.bindSource(iframe.contentWindow)`.
 *  4. Inbound messages flow through `host.handleMessage(event)`.
 *  5. Outbound DATA_RESPONSE / ERROR / READY are queued via
 *     `sendDataResponse`, `sendReady`, etc. The hook's dispatcher
 *     implementation issues the actual `iframe.contentWindow.postMessage`
 *     calls.
 *  6. On unmount, the listener is removed and the host's counters are
 *     reset.
 *
 * Why not `useSyncExternalStore`: the host's mutable counters are not
 * a React-observable signal; the hook only needs to re-render on
 * three specific events (last error, last resize, ready/not-ready)
 * which it tracks in plain `useState`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';

import {
  PostMessageHost,
  type ArgusLiveHostDispatcher,
  type ArgusLiveHostLogger,
  type HostMessageOutcome,
  consoleHostLogger,
} from './post-message-host-core';
import type {
  HostToIframeEvent,
  IframeToHostEventType,
} from '@argusai/ai-core/argus-live/runtime/post-message-protocol';
import { apiClient } from '../../lib/api-client';

// ─── Default dispatcher: posts to AL-B2's endpoints ────────────────────

/**
 * Production dispatcher. Issues two REST calls:
 *   - POST /argus-live/dashboards/:id/data-fetch
 *   - POST /argus-live/dashboards/:id/audit-events
 *
 * Both endpoints are owned by AL-B2 (data-fetch proxy) and live under
 * the `/argus-live/dashboards/:id/...` prefix — dashboardId is in the
 * URL (RLS / view-grant gates key off it), and the request body carries
 * only the binding-specific shape.
 *
 * Note on the prefix: `apiClient` auto-prepends `/api/v1` (see
 * `lib/api-client.ts`), so callers MUST pass paths without it. A
 * regression here (passing `/api/v1/...` again) double-prefixes to
 * `/api/v1/api/v1/...` and every fetch silently 404s — exactly the
 * S3 P0 #1 bug this dispatcher was rewritten to fix.
 *
 * Body shapes match the server's strict() Zod schemas:
 *   - data-fetch: `{bindingKey, params?, versionId?}` (requestId is host
 *     ↔ iframe correlation only; the server never sees it).
 *   - audit-events: `{eventType, metadata?}` where eventType is the
 *     short iframe-protocol form (e.g. `runtime.crashed`); the server
 *     maps to its `argus_live.runtime.crashed` audit event.
 *
 * Tests inject a fake dispatcher so the real `apiClient` is never
 * touched.
 */
/**
 * Exported for tests so the URL + body contract can be exercised as
 * pure logic without renderHook (web package's vitest suite is
 * jsdom-free; see use-argus-live.grants.test.ts §3).
 */
export function buildDefaultDispatcher(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  logger: ArgusLiveHostLogger,
  getVersionId?: () => string | null | undefined,
): ArgusLiveHostDispatcher {
  return {
    async fetchData(input) {
      // bindingId on the wire is named bindingKey server-side — same
      // value, different label (server treats it as opaque). requestId
      // is dropped: it's used by the postMessage host to correlate the
      // iframe's request with the eventual DATA_RESPONSE, NOT by the
      // server.
      return await apiClient.post<{
        data: unknown;
        classification: { sensitivity: string; exportControl: string };
        dataAsOf: string;
      }>(
        `/argus-live/dashboards/${input.dashboardId}/data-fetch`,
        (() => {
          const resolvedVersionId = input.versionId ?? getVersionId?.();
          return {
            bindingKey: input.bindingId,
            params: input.params,
            ...(resolvedVersionId ? { versionId: resolvedVersionId } : {}),
          };
        })(),
      );
    },
    async executeAction(input) {
      // Cockpit Phase 3 (§2.C) — POST the execute-action endpoint. The server
      // mints the ExecutionAuthorization (the iframe never mints authz). A
      // FORBIDDEN / ERROR HTTP status returns a structured { ok:false, status,
      // error } body which apiClient surfaces as a thrown error; we normalize
      // both success + error bodies into the dispatcher contract so the host
      // always relays a settled ACTION_RESULT (never a hang).
      try {
        const res = await apiClient.post<
          | { ok: true; status: 'executed'; result: unknown }
          | { ok: true; status: 'pending_approval'; approvalId: string }
        >(
          `/argus-live/dashboards/${input.dashboardId}/actions/${input.actionId}/execute`,
          { inputOverride: input.inputOverride },
        );
        if (res.status === 'pending_approval') {
          return { status: 'pending_approval', approvalId: res.approvalId };
        }
        return { status: 'executed', result: res.result };
      } catch (err) {
        // apiClient throws on 4xx/5xx with the parsed JSON body on `.data`.
        // Recover the structured { ok:false, status, error } body so the
        // dashboard can render forbidden vs error (read_only → 403 forbidden,
        // gateway/workflow failure → 502 error).
        const data = (err as {
          data?: { status?: string; error?: { code?: string; message?: string } };
        }).data;
        const status =
          data?.status === 'forbidden' ? 'forbidden' : ('error' as const);
        return {
          status,
          code: data?.error?.code ?? 'INTERNAL_ERROR',
          message:
            data?.error?.message ??
            (err as Error).message ??
            'Action execution failed',
        };
      }
    },
    async stateOp(input) {
      // Cockpit Phase 4 (§2.A) — route to the STATE endpoints. The server sets
      // organization_id (the iframe never writes the DB) and classification-
      // gates every row. A FORBIDDEN / ERROR HTTP status returns a structured
      // { ok:false, error } body which apiClient surfaces as a thrown error; we
      // normalize both success + error into the dispatcher contract so the host
      // always relays a settled STATE_RESPONSE (never a hang).
      try {
        if (input.op === 'read_rows') {
          const qs = new URLSearchParams();
          qs.set('tableSlug', input.tableSlug);
          if (input.query?.filter) {
            qs.set('filter', JSON.stringify(input.query.filter));
          }
          if (typeof input.query?.limit === 'number') {
            qs.set('limit', String(input.query.limit));
          }
          if (input.query?.cursor) qs.set('cursor', input.query.cursor);
          const res = await apiClient.get<{
            ok: true;
            rows: unknown[];
            nextCursor: string | null;
            excludedByClearance: number;
          }>(
            `/argus-live/dashboards/${input.dashboardId}/state?${qs.toString()}`,
          );
          return {
            status: 'ok',
            data: {
              rows: res.rows,
              nextCursor: res.nextCursor,
              excludedByClearance: res.excludedByClearance,
            },
          };
        }
        if (input.op === 'write_row') {
          const res = await apiClient.post<{
            ok: true;
            rowId: string;
            rowKey: string;
          }>(`/argus-live/dashboards/${input.dashboardId}/state`, {
            tableSlug: input.tableSlug,
            rowKey: input.rowKey,
            data: input.data ?? {},
            ...(input.classification
              ? { classification: input.classification }
              : {}),
          });
          return {
            status: 'ok',
            data: { rowId: res.rowId, rowKey: res.rowKey },
          };
        }
        if (input.op === 'read_user_state') {
          const qs = new URLSearchParams();
          qs.set('tableSlug', input.tableSlug);
          if (input.rowKey) qs.set('rowKey', input.rowKey);
          const res = await apiClient.get<{
            ok: true;
            state: unknown;
            updatedAt: string | null;
          }>(
            `/argus-live/dashboards/${input.dashboardId}/user-state?${qs.toString()}`,
          );
          return { status: 'ok', data: { state: res.state, updatedAt: res.updatedAt } };
        }
        // write_user_state
        const res = await apiClient.patch<{
          ok: true;
          state: unknown;
          updatedAt: string | null;
        }>(`/argus-live/dashboards/${input.dashboardId}/user-state`, {
          tableSlug: input.tableSlug,
          patch: input.data ?? {},
          ...(input.rowKey ? { rowKey: input.rowKey } : {}),
        });
        return { status: 'ok', data: { state: res.state, updatedAt: res.updatedAt } };
      } catch (err) {
        const data = (err as {
          data?: { error?: { code?: string; message?: string } };
        }).data;
        // A 403 → forbidden (classification / no access); everything else →
        // error. The server returns 403 for FORBIDDEN / CLASSIFICATION_DENIED /
        // CLEARANCE_UNVERIFIED.
        const httpStatus = (err as { status?: number }).status;
        const status = httpStatus === 403 ? 'forbidden' : ('error' as const);
        return {
          status,
          code: data?.error?.code ?? 'INTERNAL_ERROR',
          message:
            data?.error?.message ??
            (err as Error).message ??
            'State operation failed',
        };
      }
    },
    async emitAudit(input) {
      // The iframe-protocol eventType is `dashboard.runtime.<verb>`
      // (e.g. `dashboard.runtime.crashed`). The server route accepts the
      // short form (`runtime.crashed`) and maps to the canonical
      // `argus_live.runtime.crashed` audit event. Strip the
      // `dashboard.` prefix here so the wire payload matches the
      // route's strict() BodySchema.
      const shortEventType = input.eventType.replace(/^dashboard\./, '');
      await apiClient.post(
        `/argus-live/dashboards/${input.dashboardId}/audit-events`,
        {
          eventType: shortEventType,
          metadata: input.metadata,
        },
      );
    },
    sendToIframe(event) {
      const win = iframeRef.current?.contentWindow;
      if (!win) {
        logger.warn(
          { eventType: event.type },
          'argus-live:hook iframe contentWindow not ready — message dropped',
        );
        return;
      }
      try {
        // Null-origin sandbox — targetOrigin must be '*' because the
        // iframe's origin is the string 'null' and Chrome rejects
        // 'null' as a targetOrigin string. Identity-on-source is
        // enforced by the iframe runtime in turn.
        win.postMessage(event, '*');
      } catch (err) {
        logger.error(
          { err: (err as Error).message },
          'argus-live:hook iframe.postMessage failed',
        );
      }
    },
  };
}

// ─── Hook public surface ───────────────────────────────────────────────

export interface DataResponseRecord {
  requestId: string;
  bindingId: string;
  /** Host-side wallclock when DATA_RESPONSE was sent to the iframe. */
  sentAt: number;
}

export interface HookErrorRecord {
  reason: string;
  details?: string;
  at: number;
}

export interface UseArgusLiveDashboardHostOptions {
  /** Inject a custom dispatcher (used by AL-B5 e2e for fakes; default
   *  posts to AL-B2's REST endpoints). */
  dispatcher?: ArgusLiveHostDispatcher;
  /** Inject a logger (used by AL-B5 + unit tests; default delegates
   *  to `console`). */
  logger?: ArgusLiveHostLogger;
  /** Inject a clock for deterministic tests. */
  now?: () => number;
  /** Optional version pin for data-fetch calls from a rendered iframe snapshot. */
  versionId?: string | null;
}

export interface UseArgusLiveDashboardHostReturn {
  /** Whether the iframe has sent the READY message. UI-pane code can
   *  show a loading skeleton until this is true. */
  isReady: boolean;
  /** Last RESIZE height (capped). Apply via inline style on the iframe's
   *  container if you want auto-grow. */
  lastResize: number | null;
  /** Rolling list of DATA_RESPONSE sends. Bounded to 50 entries; the
   *  iframe is the authoritative store of its data, this is just an
   *  observation log for dev tools / debug pane. */
  dataResponses: DataResponseRecord[];
  /** Rolling list of host-side rejections. Bounded to 20 entries —
   *  useful for displaying a "this dashboard is being rate-limited"
   *  banner. */
  errors: HookErrorRecord[];
  /**
   * Send a Host→Iframe ready/init payload. Called by AL-B3 immediately
   * after the version's bindings + classification + theme are loaded.
   * Naming mirrors the protocol's `READY` (host-direction).
   */
  sendReady: (
    payload: Omit<
      Extract<HostToIframeEvent, { type: 'READY' }>,
      'type'
    >,
  ) => void;
  /**
   * Forward a DATA_RESPONSE manually — used by tests / e2e fakes that
   * want to short-circuit a request without going through the
   * dispatcher.
   */
  sendDataResponse: (
    payload: Omit<
      Extract<HostToIframeEvent, { type: 'DATA_RESPONSE' }>,
      'type'
    >,
  ) => void;
  /** Diagnostic snapshot from the underlying host. */
  snapshot: () => ReturnType<PostMessageHost['snapshot']>;
  /** Dispatcher used by the host; subscribe-mode bindings reuse it to refetch. */
  dispatcher: ArgusLiveHostDispatcher | null;
}

/**
 * Sets up the host listener for the lifetime of the iframe.
 *
 * Re-creates the underlying `PostMessageHost` instance whenever the
 * `dashboardId` changes (so opening a different dashboard does not
 * inherit the previous one's rate-limit counters).
 */
export function useArgusLiveDashboardHost(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  dashboardId: string,
  options: UseArgusLiveDashboardHostOptions = {},
): UseArgusLiveDashboardHostReturn {
  const [isReady, setIsReady] = useState(false);
  const [lastResize, setLastResize] = useState<number | null>(null);
  const [dataResponses, setDataResponses] = useState<DataResponseRecord[]>(
    [],
  );
  const [errors, setErrors] = useState<HookErrorRecord[]>([]);

  // Logger and dispatcher must be stable across renders or the host
  // would tear down + re-create on every re-render. We capture the
  // initial value in refs.
  const loggerRef = useRef<ArgusLiveHostLogger>(
    options.logger ?? consoleHostLogger,
  );
  const versionIdRef = useRef<string | null>(options.versionId ?? null);
  versionIdRef.current = options.versionId ?? null;
  const dispatcherRef = useRef<ArgusLiveHostDispatcher | null>(null);

  // Build the dispatcher exactly once. The default dispatcher closes
  // over `iframeRef`, which is stable across renders (React docs
  // guarantee `useRef` returns the same object).
  if (dispatcherRef.current === null) {
    dispatcherRef.current =
      options.dispatcher ??
      buildDefaultDispatcher(
        iframeRef,
        loggerRef.current,
        () => versionIdRef.current,
      );
  }

  // Wrap the dispatcher so we can intercept `sendToIframe` for the
  // host's `dataResponses` log. This wrapper is built once per host
  // instance below.
  const host = useMemo(() => {
    const baseDispatcher = dispatcherRef.current!;
    const wrappedDispatcher: ArgusLiveHostDispatcher = {
      fetchData: (input) =>
        baseDispatcher.fetchData({
          ...input,
          versionId: input.versionId ?? versionIdRef.current ?? undefined,
        }),
      executeAction: baseDispatcher.executeAction.bind(baseDispatcher),
      stateOp: baseDispatcher.stateOp.bind(baseDispatcher),
      emitAudit: baseDispatcher.emitAudit.bind(baseDispatcher),
      sendToIframe: (event) => {
        if (event.type === 'DATA_RESPONSE') {
          setDataResponses((prev) =>
            cap(
              [
                ...prev,
                {
                  requestId: event.requestId,
                  bindingId: event.bindingId,
                  sentAt: Date.now(),
                },
              ],
              50,
            ),
          );
        }
        baseDispatcher.sendToIframe(event);
      },
    };
    const instance = new PostMessageHost({
      dashboardId,
      dispatcher: wrappedDispatcher,
      logger: loggerRef.current,
      now: options.now,
    });
    // Wire resize callback for state propagation.
    instance.onResizeCallback = (h) => setLastResize(h);
    return instance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId]);

  // Bind the iframe contentWindow on every `load`. The iframe may
  // reload (via dashboard refresh) so we re-bind defensively.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const handleLoad = () => {
      host.bindSource(iframe.contentWindow);
      // Reset state on (re)mount.
      setIsReady(false);
      setLastResize(null);
      setDataResponses([]);
      setErrors([]);
      host.resetCounters();
    };
    iframe.addEventListener('load', handleLoad);
    // If the iframe is already loaded by the time the listener
    // attaches (e.g. SSR-hydrated), bind immediately.
    if (iframe.contentWindow) {
      host.bindSource(iframe.contentWindow);
    }
    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [host, iframeRef]);

  // Main `message` listener — attached at the window level (postMessage
  // bubbles to `window`, not the iframe).
  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR safety
    const onMessage = (ev: MessageEvent) => {
      void host.handleMessage(ev).then((outcome: HostMessageOutcome) => {
        if (outcome.kind === 'rejected') {
          setErrors((prev) =>
            cap(
              [
                ...prev,
                {
                  reason: outcome.reason,
                  details: outcome.details,
                  at: Date.now(),
                },
              ],
              20,
            ),
          );
          return;
        }
        if (outcome.type === 'READY') {
          setIsReady(true);
        }
      });
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [host]);

  // Stable callback wrappers around `dispatcher.sendToIframe`.
  const sendReady = useCallback<UseArgusLiveDashboardHostReturn['sendReady']>(
    (payload) => {
      dispatcherRef.current?.sendToIframe({ type: 'READY', ...payload });
    },
    [],
  );
  const sendDataResponse = useCallback<
    UseArgusLiveDashboardHostReturn['sendDataResponse']
  >((payload) => {
    dispatcherRef.current?.sendToIframe({
      type: 'DATA_RESPONSE',
      ...payload,
    });
  }, []);
  const snapshot = useCallback(() => host.snapshot(), [host]);

  return {
    isReady,
    lastResize,
    dataResponses,
    errors,
    sendReady,
    sendDataResponse,
    snapshot,
    dispatcher: dispatcherRef.current,
  };
}

// ─── Small internals ────────────────────────────────────────────────

function cap<T>(arr: T[], max: number): T[] {
  return arr.length <= max ? arr : arr.slice(arr.length - max);
}

// Re-export for downstream consumers (AL-B3 + AL-B5).
export type {
  ArgusLiveHostDispatcher,
  ArgusLiveHostLogger,
} from './post-message-host-core';

// IframeToHostEventType is used by callers wanting to type-narrow
// outcome.type without importing from ai-core directly.
export type { IframeToHostEventType };
